import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PatientContext } from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext";
import { useFetch } from "../../services/hooks/useFetch";
import { projectsPrefix } from "../../services/ProjectsServices";
import { projectTypes } from "../../utils/main";
import CodeListDataGrid from "../codelist/CodeListDataGrid";
import IndexProcedureVisitCardSkeleton from "./skeletons/IndexProcedureVisitCardSkeleton";
import FlowsheetDataTable from "./tables/FlowsheetDataTable";
import LabDataTable from "./tables/LabDataTable.jsx";
import OtherDevicesUsedTable from "./tables/OtherDevicesUsedTable.tsx";
import ProcedureTimeline from "./timeline/ProcedureTimeline";
import VisitsDrawer from "./VisitsDrawer";

export default function IndexProcedureVisitCard() {
  const { id, devices, procedureDate } = useContext(PatientContext) ?? {};
  const {
    type: projectType,
    procedureTimelineEnabled,
    codeOffsetEnabled,
  } = useContext(ProjectContext) ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const refreshIndexProcedure = searchParams.get("refreshIndexProcedure");
  const projectId = searchParams.get("projectId");

  const [open, setOpen] = useState(false);

  const {
    data: indexEncounter,
    isLoading: isEncounterLoading,
    fetch: fetchEncounter,
    reset: resetEncounter,
  } = useFetch<EncounterSearchResult>();

  function toggleOpen() {
    setOpen((prev) => !prev);
  }

  const removeSearchParam = useCallback(
    (param: string) => {
      searchParams.delete(param);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const getIndexProcedure = useCallback(() => {
    void fetchEncounter(
      `${projectsPrefix}/${projectId}/patients/${id}/encounters/index`,
    );
  }, [projectId, id, fetchEncounter]);

  useEffect(() => {
    if (refreshIndexProcedure) {
      removeSearchParam("refreshIndexProcedure");
      return;
    }
    if (!id) {
      resetEncounter();
      return;
    }
    if (!indexEncounter && !isEncounterLoading) {
      getIndexProcedure();
    }
  }, [
    projectId,
    id,
    refreshIndexProcedure,
    removeSearchParam,
    getIndexProcedure,
    indexEncounter,
    isEncounterLoading,
    resetEncounter,
  ]);

  const descriptions = devices ? devices.map((item) => item.description) : [];

  if (isEncounterLoading || !id || isEncounterLoading || !indexEncounter) {
    return <IndexProcedureVisitCardSkeleton />;
  }

  return (
    <>
      {open && indexEncounter && (
        <VisitsDrawer
          toggleDrawer={() => toggleOpen()}
          initialVisitId={indexEncounter.id}
        />
      )}
      {indexEncounter && (
        <div className={"index-procedure-visit-container"}>
          <Card className={"index-procedure-visit-card"}>
            <CardHeader
              title={
                <Typography
                  className="card-title"
                  align={"left"}
                  variant={"h5"}
                >
                  Index Procedure Visit
                </Typography>
              }
              action={
                <Link
                  className={"index-procedure-link"}
                  component={"button"}
                  variant={"subtitle2"}
                  onClick={toggleOpen}
                >
                  Open Visit Details
                </Link>
              }
            ></CardHeader>
            <CardContent
              classes={{ root: "content" }}
              sx={{ padding: 0, textAlign: "left" }}
            >
              <Stack spacing={2}>
                <div className={"section"}>
                  <Typography className={"visit-date-label"} variant="h6">
                    Procedure Year
                  </Typography>
                  <Typography className={"visit-date"} variant={"body1"}>
                    {procedureDate && new Date(procedureDate).getUTCFullYear()}
                  </Typography>
                </div>
                {projectType === projectTypes.device &&
                  procedureTimelineEnabled &&
                  !!indexEncounter && (
                    <div className="procedure-timeline">
                      <ProcedureTimeline indexEncounter={indexEncounter} />
                    </div>
                  )}
                {projectType === projectTypes.device && (
                  <div className="device-details">
                    <Typography className="device-details-title" variant="h6">
                      Device Details
                    </Typography>
                    <Typography
                      variant="body2"
                      className="device-details-normal"
                    >
                      Version / Model #:{" "}
                      <Box component="span" fontWeight="bold" fontSize="16px">
                        {descriptions.join(", ")}
                      </Box>
                    </Typography>
                    <div className="device-details-used">
                      <Typography
                        variant="body2"
                        className="device-details-normal"
                      >
                        Devices Used:{" "}
                        <Box component="span" fontWeight="bold" fontSize="16px">
                          {indexEncounter?.suppliesUsed}
                        </Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        className="device-details-normal"
                      >
                        Devices Wasted:{" "}
                        <Box component="span" fontWeight="bold" fontSize="16px">
                          {indexEncounter?.suppliesWasted}
                        </Box>
                      </Typography>
                    </div>
                  </div>
                )}
                <OtherDevicesUsedTable encounter={indexEncounter} />
                <div className={"section diagnosis"}>
                  <Typography className="section-title" variant="h6">
                    <span className="section-title">Diagnoses</span>
                    <span className="section-subtitle">
                      &nbsp;for index procedure visit
                    </span>
                  </Typography>
                  {indexEncounter?.diagnosisCodes &&
                    indexEncounter?.diagnosisCodes.length > 0 && (
                      <CodeListDataGrid
                        codes={indexEncounter?.diagnosisCodes}
                        isCodeOffsetEnabled={codeOffsetEnabled ?? false}
                        tableId="index-diagnosis-codes"
                      />
                    )}
                </div>
                <div className={"section procedure"}>
                  <Typography className="section-title" variant="h6">
                    <span className="section-title">Procedures</span>
                    <span className="section-subtitle">
                      &nbsp;for index procedure visit
                    </span>
                  </Typography>
                  {indexEncounter?.procedureCodes &&
                    indexEncounter?.procedureCodes.length > 0 && (
                      <CodeListDataGrid
                        codes={indexEncounter?.procedureCodes}
                        isCodeOffsetEnabled={codeOffsetEnabled ?? false}
                        tableId="index-procedure-codes"
                      />
                    )}
                </div>
                <FlowsheetDataTable
                  encounterId={"index"}
                  countTotal={indexEncounter.flowsheetCount?.total}
                  highlightTotal={indexEncounter.flowsheetCount?.highlight}
                />
                <LabDataTable
                  encounterId={"index"}
                  countTotal={indexEncounter.labCount?.total}
                  highlightTotal={indexEncounter.labCount?.highlight}
                />
              </Stack>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
